import type { Component, ComponentProps } from '@/types/component';
import type NextImage from 'next/image';
import type { ReactEventHandler } from 'react';

import { useAppState } from '@/hooks/useAppState';
import { getBackendImageServer } from '@/utils/getBackendImageServer';
import { getGridSystemConfig } from '@/utils/getGridSystemConfig';
import { pxToNumber } from '@/utils/pxConverter';
import { tw } from '@/utils/tw';
import { omit } from 'lodash-es';
import Head from 'next/head';
import { getImageProps } from 'next/image';
import { useCallback, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { isString, isUndefined } from 'typesafe-utils';
import { getImageDimensions } from '../utils/getImageDimensions';
import { getImageSrcSet } from '../utils/getImageSrcSet';

interface BaseProps extends Partial<ComponentProps<typeof NextImage>> {
  isGif?: boolean;
  maxWidth?: number;
}

const defaults: BaseProps = {
  placeholder: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1NjggMjg5IiBmaWxsPSIjZWVlIj48cGF0aCBkPSJNMCAwaDU2OHYyODlIMHoiLz48ZyBmaWxsPSIjODg4Ij48Y2lyY2xlIGN4PSIyNTkiIGN5PSIxNDUiIHI9IjkiLz48Y2lyY2xlIGN4PSIyODQiIGN5PSIxNDUiIHI9IjkiLz48Y2lyY2xlIGN4PSIzMDkiIGN5PSIxNDUiIHI9IjkiLz48L2c+PC9zdmc+`,
};

export const BaseSlot: Component<BaseProps> = ({ alt, src, isGif, onError, onLoad, className, style, ...props }) => {
  const [{ isMobile }] = useAppState();
  const [state, setState] = useState<'start' | 'ready' | 'error'>('start');
  const ref = useRef<HTMLImageElement>(null);
  const gridSystem = getGridSystemConfig();

  const placeholder = props.placeholder || defaults.placeholder;

  const dimensions =
    props.width && props.height
      ? {
          width: Number(props.width),
          height: Number(props.height),
        }
      : getImageDimensions(src);

  const maxWidth = Math.min(
    dimensions.width || Infinity,
    props.maxWidth || Infinity,
    pxToNumber(gridSystem.DEFAULT.maxContentWidth),
  );

  const scale = dimensions.width && maxWidth ? Math.min(maxWidth / dimensions.width, 1) : 1;
  const isGifImage = Boolean(isGif);
  const hasDimensions = !isUndefined(dimensions.width) && !isUndefined(dimensions.height);
  const requestedFill = Boolean(props.fill);
  const mustFillBecauseNoDimensions = !hasDimensions;
  // Known-size GIFs reject fill so they keep natural sizing instead of absolute fill layout.
  const allowFill = !isGifImage || mustFillBecauseNoDimensions;
  const fill = allowFill && (requestedFill || mustFillBecauseNoDimensions);
  const width = fill ? undefined : dimensions.width ? dimensions.width * scale : undefined;
  const height = fill ? undefined : dimensions.height ? dimensions.height * scale : undefined;
  const referrerPolicy = isString(src) && src.startsWith(getBackendImageServer()) ? 'no-referrer' : undefined;

  const { props: imageProps } = getImageProps({
    ...omit(props, 'maxWidth'),
    src: src || '',
    alt: alt || '',
    fill,
    width,
    height,
    placeholder: state === 'start' ? placeholder : 'empty',
    referrerPolicy,
    fetchPriority: props.priority ? 'high' : undefined,
  });

  const srcSet = state !== 'error' ? getImageSrcSet(src, maxWidth) : undefined;
  const sizes = srcSet ? `min(${100 / (isMobile ? 2 : 1)}vw, ${maxWidth}px)` : undefined;

  const handleError = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      setState('error');
      onError?.(event);
    },
    [onError],
  );

  const handleLoad = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      if (state === 'start') {
        setState('ready');
      }

      onLoad?.(event);
    },
    [onLoad, state],
  );

  useEffectOnce(() => {
    const image = ref.current;

    if (image?.complete) {
      if (!image?.naturalWidth || !image?.naturalHeight) {
        setState('error');
      } else {
        setState('ready');
      }
    }
  });

  return (
    <picture
      data-fill={fill ? '' : undefined}
      data-gif={isGifImage ? '' : undefined}
      className={tw.merge(fill && 'aspect-default', className)}
      style={style}
    >
      {imageProps.priority ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={imageProps.src}
            imageSrcSet={srcSet}
            imageSizes={sizes}
            fetchPriority="high"
          />
        </Head>
      ) : null}

      <img
        className="w-full object-cover"
        {...imageProps}
        onError={handleError}
        onLoad={handleLoad}
        src={state === 'error' ? placeholder : imageProps.src}
        srcSet={srcSet}
        sizes={sizes}
        ref={ref}
      />
    </picture>
  );
};
